import { Skeleton } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { SortingParams, SortType } from 'modules/types/PagingParams';
import { useTranslation } from 'react-i18next';
import { TableHookProps } from 'modules/ui/pageWithTable/TableWithSearch';
import { SkeletonSmallSize, getMoreDetailsButton } from 'modules/utils';
import { ContractOverviewPathsEnum } from 'modules/contractsOverviewPage/routes';
import { CurrencyContainer } from 'modules/ui/table/currencyContainer/CurrencyContainer';
import { ContractGroup } from './helper';
import { ContractsSelectors, ContractsThunks } from '../redux';
import { PAGE_SIZE } from '../../api/constants';
import { useAppDispatch, useAppSelector } from '../../hooks/appHooks';
import { TableColumn } from '../../ui/table';

export const useContractsActions = (props?: TableHookProps) => {
  const { type: contractsTabGroup } = props || {};
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const contracts = useAppSelector(ContractsSelectors.selectContracts);
  const contractsGroupResponse = useAppSelector(
    ContractsSelectors.selectContractGroup,
  );
  const totalPages = useAppSelector(ContractsSelectors.selectTotalPageCount);
  const loading = useAppSelector(ContractsSelectors.selectContractsLoading);

  const goToContractDetailsPage = useCallback(
    (number: string) =>
      history.push(
        `${ContractOverviewPathsEnum.contractsOverview}/${encodeURIComponent(
          number,
        )}`,
      ),
    [history],
  );

  const fetchMoreContracts = useCallback(
    (page: number, sort?: SortingParams[], search?: string) => {
      dispatch(
        ContractsThunks.fetchContracts({
          page,
          size: PAGE_SIZE,
          search,
          contractGroup:
            (contractsTabGroup as ContractGroup) ?? ContractGroup.ALL,
          sort,
        }),
      );
    },
    [dispatch, contractsTabGroup],
  );

  const getContractsColumns = useMemo(
    () => [
      {
        label: t('contractsPage.table.number'),
        accessor: 'number',
        sort: SortType.DEFAULT,
      },
      {
        label: t('contractsPage.table.subjectName'),
        accessor: 'subjectName',
        sort: SortType.DEFAULT,
      },
      {
        label: t('contractsPage.table.leasingTypeCategory'),
        accessor: 'leasingTypeCategory',
        sort: SortType.DEFAULT,
      },
      {
        label: t('contractsPage.table.openDebt'),
        accessor: 'openDebtEur',
        sort: SortType.DEFAULT,
      },
      {
        label: t('contractsPage.table.moreDetails'),
        accessor: 'moreDetails',
        mobileOnly: true,
      },
    ],
    [t],
  );

  const getContractsObject = useMemo(
    () =>
      contracts?.map((contract) => ({
        ...contract,
        openDebtEur: <CurrencyContainer value={contract.openDebtEur} />,
        moreDetails: getMoreDetailsButton(() =>
          goToContractDetailsPage(contract.number),
        ),
      })) || [],
    [contracts, goToContractDetailsPage],
  );

  const getSkeletonObject = useMemo(
    () =>
      [...Array(10)].map(() => ({
        number: <Skeleton.Input active style={SkeletonSmallSize} />,
        subjectName: <Skeleton.Input active style={SkeletonSmallSize} />,
        leasingTypeCategory: (
          <Skeleton.Input active style={SkeletonSmallSize} />
        ),
        openDebtEur: <Skeleton.Input active style={SkeletonSmallSize} />,
        moreDetails: undefined,
      })) || [],
    [],
  );

  const [tableColumns, setTableColumns] =
    useState<TableColumn[]>(getContractsColumns);

  const [tableRows, setTableRows] = useState(getContractsObject);

  const [emptyMessageText, setEmptyMessageText] = useState<string>('');

  useEffect(() => {
    setTableColumns(getContractsColumns);
  }, [getContractsColumns, contractsTabGroup]);

  useEffect(() => {
    // to avoid race condition
    if (contractsTabGroup === contractsGroupResponse || loading) {
      setTableRows(getContractsObject);
    }

    switch (contractsTabGroup) {
      case ContractGroup.ACTIVE:
        setEmptyMessageText(t('contractsPage.empty.noActiveContracts'));
        break;
      case ContractGroup.INACTIVE:
        setEmptyMessageText(t('contractsPage.empty.noInactiveContracts'));
        break;
      case ContractGroup.ALL:
        setEmptyMessageText(t('contractsPage.empty.noAllContracts'));
        break;
    }
  }, [
    getContractsColumns,
    getContractsObject,
    contractsTabGroup,
    t,
    contractsGroupResponse,
    loading,
  ]);

  return {
    skeletonObject: getSkeletonObject,
    fetchMoreData: fetchMoreContracts,
    loading,
    totalPages,
    tableColumns,
    tableRows,
    emptyMessageText,
    setTableColumns,
  };
};
